import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CategoryGrid = () => {
  const categories = [
    {
      id: 1,
      name: "Isolatie",
      description: "Verbeter de isolatie van uw woning voor meer comfort en lagere energiekosten",
      icon: "Home",
      image: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&h=300&fit=crop",
      alt: "Worker installing pink insulation material in wall cavity for home energy efficiency",
      providerCount: "3.156",
      benefit: "30% minder energieverbruik",
      path: "/services/insulation",
      color: "from-purple-500/10 to-pink-500/10",
      iconColor: "text-purple-600"
    },
    {
      id: 2,
      name: "Warmtepomp",
      description: "Efficiënt verwarmen en koelen met moderne warmtepomp technologie",
      icon: "Thermometer",
      image: "/assets/images/Untitled-1768596855661.jpeg",
      alt: "Modern heat pump unit installed outside residential building for sustainable heating",
      providerCount: "1.523",
      benefit: "Tot 70% energiebesparing",
      path: "/services/heatpump",
      color: "from-blue-500/10 to-cyan-500/10",
      iconColor: "text-blue-600"
    },
    {
      id: 3,
      name: "Zonnepanelen",
      description: "Wek uw eigen duurzame energie op met zonnepanelen op uw dak",
      icon: "Sun",
      image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400&h=300&fit=crop",
      alt: "Blue solar panels installed on residential roof generating renewable energy under bright sunlight",
      providerCount: "2.847",
      benefit: "Tot €1.500 besparing per jaar",
      path: "/services/solar",
      color: "from-yellow-500/10 to-orange-500/10",
      iconColor: "text-yellow-600"
    },
    {
      id: 4,
      name: "Thuisbatterij",
      description: "Sla uw eigen opgewekte energie op voor later gebruik",
      icon: "Battery",
      image: "/assets/images/GRP-Wall-1783593982170.png",
      alt: "GRP Wall thuisbatterij — moderne wandgemonteerde energieopslag voor thuis",
      providerCount: "892",
      benefit: "100% energieonafhankelijk",
      path: "/services/battery",
      color: "from-green-500/10 to-emerald-500/10",
      iconColor: "text-green-600"
    },
    {
      id: 5,
      name: "Ventilatie",
      description: "Gezonde binnenlucht met moderne ventilatiesystemen",
      icon: "Wind",
      image: "/assets/images/Untitled-1768597099743.jpeg",
      alt: "Modern ventilation system with white air ducts and fans ensuring healthy indoor air quality",
      providerCount: "1.234",
      benefit: "Gezonder woonklimaat",
      path: "/services/ventilation",
      color: "from-cyan-500/10 to-blue-500/10",
      iconColor: "text-cyan-600"
    },
    {
      id: 6,
      name: "Waterontharder",
      description: "Minder kalkaanslag, langere levensduur van apparaten en meer comfort in huis",
      icon: "Droplets",
      image: "/assets/images/Untitled-1768597678687.jpeg",
      alt: "Water softener before and after comparison showing lime scale buildup reduction and cleaner water quality",
      providerCount: "1.089",
      benefit: "Minder kalk, meer comfort",
      path: "/services/water-softener",
      color: "from-blue-500/10 to-cyan-500/10",
      iconColor: "text-blue-600"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const imageVariants = {
    hover: {
      scale: 1.1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    hover: {
      rotate: [0, -10, 10, -10, 0],
      scale: 1.15,
      transition: {
        duration: 0.5,
        ease: "easeInOut"
      }
    }
  };

  const benefitVariants = {
    hover: {
      scale: 1.08,
      x: 5,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background relative overflow-hidden">
      {/* Animated background grid */}
      <motion.div 
        className="absolute inset-0 opacity-30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      </motion.div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-8 sm:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <motion.h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-3 sm:mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Populaire verduurzamingsoplossingen
          </motion.h2>
          <motion.p 
            className="text-base sm:text-lg lg:text-xl font-body text-muted-foreground max-w-3xl mx-auto px-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Ontdek welke oplossing het beste bij uw woning past
          </motion.p>
        </motion.div>

        {/* Categories Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {categories?.map((category) => (
            <motion.div
              key={category?.id}
              variants={cardVariants}
              whileHover={{ 
                y: -20, 
                scale: 1.04,
                rotateX: 5,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 15px rgba(59, 130, 246, 0.1)",
                transition: { duration: 0.4, ease: "easeOut" } 
              }}
              className="group block bg-card border border-border rounded-xl overflow-hidden shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15)] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] transition-all duration-300 transform perspective-1000"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <motion.div
                  variants={imageVariants}
                  whileHover="hover"
                  className="w-full h-full"
                >
                  <Image
                    src={category?.image}
                    alt={category?.alt}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <motion.div 
                  className={`absolute inset-0 bg-gradient-to-br ${category?.color} opacity-70`}
                  whileHover={{ opacity: 0.85 }}
                  transition={{ duration: 0.4 }}
                ></motion.div>
                
                {/* Icon Overlay */}
                <motion.div 
                  className="absolute top-4 right-4"
                  variants={iconVariants}
                  whileHover="hover"
                >
                  <motion.div 
                    className="w-12 h-12 bg-white/95 rounded-full flex items-center justify-center shadow-elevation-2"
                    whileHover={{ 
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                      scale: 1.15,
                      rotate: 360,
                      z: 50
                    }}
                    transition={{ duration: 0.5 }}
                    style={{ transform: 'translateZ(20px)' }}
                  >
                    <Icon name={category?.icon} size={24} className={category?.iconColor} />
                  </motion.div>
                </motion.div>

                {/* Animated shine effect on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                />
              </div>

              {/* Content */}
              <div className="p-6" style={{ transform: 'translateZ(10px)' }}>
                <div className="flex items-center justify-between mb-3">
                  <motion.h3 
                    className="text-xl font-heading font-bold text-foreground group-hover:text-primary transition-colors"
                    whileHover={{ scale: 1.05, x: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    {category?.name}
                  </motion.h3>
                  <motion.div 
                    className="text-xs font-caption text-muted-foreground bg-muted px-2 py-1 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
                    whileHover={{ scale: 1.1, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
                    style={{ transform: 'translateZ(15px)' }}
                  >
                    <span className="text-sm font-body font-medium text-success">{category?.benefit}</span>
                  </motion.div>
                </div>

                {/* CTAs */}
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <Link to={category?.path}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Button variant="outline" size="sm" iconName="BookOpen" iconPosition="left" fullWidth>
                        Lees meer
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to={category?.path}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Button size="sm" iconName="Users" iconPosition="left" fullWidth>
                        Bekijk aanbieders ({category?.providerCount})
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default CategoryGrid;